import { ApiRecord, field, formatInr, rec } from '../../../models/api-response.model';
import {
  parsePropertyFinanceAmount,
  resolvePropertyAmountWithGst,
} from '../../../utils/property-finance-amount.util';
import { LocationItem } from '../../../services/location.service';
import {
  isUuidLikeFileName,
  sanitizeOriginalFileName,
  toSentenceCase,
} from '../../../utils/upload-display-name.util';
import type { IdpAadhaarExtractedData } from '../utils/document-verification-parsers';
import { maskPanForDisplay } from './co-owner-flow.util';
import { DocumentUploadRow, UploadedDocumentFile } from './property-owner-documents/property-owner-documents.component';
import { isLocationUuid } from './utils/location-form.util';
import type { PropertyOwnerPanKycFields } from './utils/property-owner-kyc.helpers';
import { resolveBookingUnitCategoryFromApi } from '../utils/booking-unit-category.util';

export interface ResidentialAddressInput {
  addressLine?: string;
  city?: string;
  state?: string;
  country?: string;
  pinCode?: string;
}

export interface LocationLookupOptions {
  countries?: LocationItem[];
  states?: LocationItem[];
  cities?: LocationItem[];
}

/** Resolves a location id to a display name; omits unresolved UUIDs. */
export function resolveLocationDisplayName(
  value: string | undefined,
  items: LocationItem[] = [],
): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }
  const byId = items.find((item) => item.id === raw);
  if (byId?.name) {
    return byId.name;
  }
  if (isLocationUuid(raw)) {
    return '';
  }
  return raw;
}

/** Converts DD/MM/YYYY or DD-MM-YYYY to ISO 8601 (YYYY-MM-DD) for backend validation. */
export function formatToIsoDate(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) {
    return undefined;
  }

  const trimmed = dateStr.trim();
  if (!trimmed) {
    return undefined;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const parts = trimmed.split(/[/\-.]/);
  if (parts.length === 3) {
    let day = parts[0];
    let month = parts[1];
    let year = parts[2];

    if (parts[0].length === 4) {
      year = parts[0];
      month = parts[1];
      day = parts[2];
    }

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return trimmed;
}

const API_GENDER_VALUES = ['male', 'female', 'other', 'prefer_not_to_say'] as const;
export type ApiGenderValue = (typeof API_GENDER_VALUES)[number];

const DEFAULT_MARITAL_STATUS_OPTIONS = [
  'Single',
  'Married',
  'Divorced',
  'Widowed',
] as const;

/** Map API marital status (e.g. married) to form option labels (e.g. Married). */
export function normalizeMaritalStatusForDisplay(
  value: string | null | undefined,
  options: readonly string[] = DEFAULT_MARITAL_STATUS_OPTIONS,
): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const match = options.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );
  return match ?? trimmed;
}

/** Map OCR/form gender text to backend DTO enum: male | female | other | prefer_not_to_say */
export function normalizeGenderForApi(
  value: string | null | undefined,
): ApiGenderValue | undefined {
  if (!value?.trim()) {
    return undefined;
  }

  const raw = value.trim();
  const v = raw.toLowerCase();

  if (API_GENDER_VALUES.includes(v as ApiGenderValue)) {
    return v as ApiGenderValue;
  }

  if (
    v === 'm' ||
    v.includes('male') ||
    raw.includes('पुरुष') ||
    v.includes('purush') ||
    v.includes('puruṣ')
  ) {
    return 'male';
  }

  if (
    v === 'f' ||
    v.includes('female') ||
    raw.includes('महिला') ||
    v.includes('mahila') ||
    v.includes('stri') ||
    v.includes('स्त्री')
  ) {
    return 'female';
  }

  if (
    v === 't' ||
    v.includes('transgender') ||
    v.includes('trans') ||
    v === 'other'
  ) {
    return 'other';
  }

  if (v.includes('prefer') || v.includes('not to say') || v.includes('not_to_say')) {
    return 'prefer_not_to_say';
  }

  return undefined;
}

/** Merge IDP/Aadhaar OCR profile into KYC details before owner-workflow save (IDP wins for identity fields). */
export function mergeIdpAadhaarIntoKycDetails(
  panKyc: PropertyOwnerPanKycFields,
  idpProfile?: IdpAadhaarExtractedData | null,
): PropertyOwnerPanKycFields {
  const merged: PropertyOwnerPanKycFields = { ...panKyc };

  if (idpProfile?.fullName?.trim()) {
    merged['fullName'] = idpProfile.fullName.trim();
  }
  const apiGender = normalizeGenderForApi(idpProfile?.gender ?? merged['gender']);
  if (apiGender) {
    merged['gender'] = apiGender;
  }

  const idpDob = formatToIsoDate(idpProfile?.dateOfBirth);
  if (idpDob) {
    merged['dateOfBirth'] = idpDob;
  } else if (merged['dateOfBirth']?.trim()) {
    merged['dateOfBirth'] = formatToIsoDate(merged['dateOfBirth']) ?? merged['dateOfBirth'].trim();
  }

  if (idpProfile?.address?.trim() && !merged['addressLine']?.trim()) {
    merged['addressLine'] = idpProfile.address.trim();
  }
  if (idpProfile?.pincode?.trim() && !merged['pinCode']?.trim()) {
    merged['pinCode'] = idpProfile.pincode.trim();
  }

  return merged;
}

/** Extracts residential fields from an owner summary API record. */
export function residentialFromOwnerRecord(owner: ApiRecord): ResidentialAddressInput {
  return {
    addressLine: field(owner, 'addressLine'),
    city: field(owner, 'city'),
    state: field(owner, 'state'),
    country: field(owner, 'country'),
    pinCode: field(owner, 'pinCode'),
  };
}

/** Builds read-only address summary fields without exposing raw location UUIDs. */
export function buildPropertyOwnerAddressSummary(
  residential: ResidentialAddressInput,
  lookups: LocationLookupOptions = {},
): { addressDetails: string; residentialDetails: string } {
  const addressLine = String(residential.addressLine ?? '').trim();
  const pinCode = String(residential.pinCode ?? '').trim();
  const country = resolveLocationDisplayName(residential.country, lookups.countries);
  const state = resolveLocationDisplayName(residential.state, lookups.states);
  const city = resolveLocationDisplayName(residential.city, lookups.cities);
  const locationSuffix = [country, state, city].filter(Boolean).join(', ');

  let residentialDetails = addressLine;
  if (locationSuffix) {
    const alreadyContainsLocation =
      !!addressLine &&
      [country, state, city]
        .filter(Boolean)
        .every((part) => addressLine.toLowerCase().includes(part.toLowerCase()));
    if (!alreadyContainsLocation) {
      residentialDetails = [addressLine, locationSuffix].filter(Boolean).join(', ');
    }
  }

  if (pinCode && residentialDetails && !residentialDetails.includes(pinCode)) {
    residentialDetails = `${residentialDetails} - ${pinCode}`;
  } else if (pinCode && !residentialDetails) {
    residentialDetails = pinCode;
  }

  const addressDetails = addressLine || residentialDetails || '—';
  return {
    addressDetails: addressDetails || '—',
    residentialDetails: residentialDetails || addressDetails || '—',
  };
}

export function resolvePanDisplayNumber(
  data: ApiRecord,
  options?: { summaryOwner?: ApiRecord },
): string {
  const kyc = rec(data['kyc']);
  const pan = rec(kyc['pan']);
  const summaryOwner = options?.summaryOwner;

  for (const value of [
    field(data, 'panMasked'),
    field(summaryOwner, 'panMasked'),
    field(pan, 'panNumber'),
    field(kyc, 'panNumber'),
  ]) {
    if (value) {
      return value;
    }
  }

  const rawPan = field(data, 'panNumber', field(summaryOwner, 'panNumber'));
  return rawPan ? maskPanForDisplay(rawPan) : '';
}

export function hasOwnerWorkflowData(workflow: ApiRecord): boolean {
  const kyc = rec(workflow['kyc']);
  const pan = rec(kyc['pan']);
  const aadhaar = rec(kyc['aadhaar']);

  return !!(
    resolvePanDisplayNumber(workflow) ||
    extractAadhaarDisplayNumber(workflow) ||
    field(kyc, 'aadhaarNumber') ||
    field(aadhaar, 'aadhaarNumber') ||
    workflow['occupation'] ||
    workflow['financial'] ||
    field(rec(kyc['contact']), 'email')
  );
}

/** Whether owner summary from API includes at least one saved owner/co-owner party. */
export function hasSummaryOwnerPartyData(summary: ApiRecord): boolean {
  const owners = (summary['owners'] as ApiRecord[] | undefined) ?? [];
  return owners.some((owner) => {
    return !!(
      field(owner, 'fullName') ||
      field(owner, 'name') ||
      field(owner, 'panNumber') ||
      extractAadhaarDisplayNumber(owner)
    );
  });
}

export function extractAadhaarDisplayNumber(
  data: ApiRecord,
  options?: { summaryOwner?: ApiRecord },
): string {
  const kyc = rec(data['kyc']);
  const aadhaar = rec(kyc['aadhaar']);
  const summaryOwner = options?.summaryOwner;

  for (const value of [
    field(aadhaar, 'aadhaarNumber'),
    field(aadhaar, 'number'),
    field(kyc, 'aadhaarNumber'),
    field(kyc, 'aadhaar_number'),
    field(data, 'aadhaarNumber'),
    field(data, 'aadharNumber'),
    field(summaryOwner, 'aadhaarNumber'),
    field(summaryOwner, 'aadhaarMasked'),
  ]) {
    if (value) {
      return value;
    }
  }

  const last4 = field(aadhaar, 'aadhaar_last4', field(kyc, 'aadhaar_last4'));
  return last4 ? `********${last4}` : '';
}

export function extractNationality(workflow: ApiRecord): string {
  const kyc = rec(workflow['kyc']);
  const aadhaar = rec(kyc['aadhaar']);
  const personal = rec(kyc['personal']);
  return field(aadhaar, 'nationality', field(personal, 'nationality', 'Indian'));
}

export const DOCUMENT_ROW_TYPE_MAP: Record<string, string> = {
  pan: 'pan_card',
  aadhar: 'aadhaar_card',
  photo: 'passport_photo',
  itr: 'form16_or_itr',
  salary: 'salary_slips',
  bank: 'bank_statements',
  loan: 'existing_loan_statements',
};

export function parseAmountInput(value: string | number | null | undefined): number {
  return parsePropertyFinanceAmount(value);
}

/** Resolves agreement value used for property/unit amount displays. */
export function resolveAgreementValueFromBooking(booking: ApiRecord): number {
  const summary = rec(booking['summary']);
  const core = rec(booking['core']);
  const pricing = rec(core['pricingSnapshot']);
  const paymentTotals = rec(booking['paymentTotals']);
  const raw =
    pricing['agreementValue'] ??
    pricing['agreement_value'] ??
    paymentTotals['agreementAmount'] ??
    paymentTotals['agreementValue'] ??
    core['agreementValue'] ??
    core['agreementAmount'] ??
    summary['agreementAmount'] ??
    summary['agreementValue'] ??
    summary['totalValue'] ??
    0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

/** Resolves property GST from booking API payload. */
export function resolveGstAmountFromBooking(booking: ApiRecord): number {
  const summary = rec(booking['summary']);
  const core = rec(booking['core']);
  const pricing = rec(core['pricingSnapshot']);
  const paymentTotals = rec(booking['paymentTotals']);
  const raw =
    pricing['gstAmount'] ??
    pricing['gst_amount'] ??
    summary['gst'] ??
    summary['gstAmount'] ??
    paymentTotals['gst'] ??
    paymentTotals['propertyGstAmount'] ??
    0;
  return parsePropertyFinanceAmount(raw);
}

/** Agreement value + GST — used for owner contribution and loan amount caps. */
export function resolveBookingPropertyAmountWithGst(booking: ApiRecord): number {
  return resolvePropertyAmountWithGst(
    resolveAgreementValueFromBooking(booking),
    resolveGstAmountFromBooking(booking),
  );
}

export function formatBookingPropertyAmountWithGst(booking: ApiRecord): string {
  const total = resolveBookingPropertyAmountWithGst(booking);
  return total > 0 ? `₹ ${formatInr(total)}` : '—';
}

export interface BookingOverviewParkingDetails {
  parkingNo: string;
  area: string;
  type: string;
  amount: string | number;
}

export interface BookingOverviewPropertyAmount {
  agreementAmount: string | number;
  gst: string | number;
  tds: string | number;
  otherCharges: string | number;
}

export interface BookingOverviewPropertyBlock {
  project: string;
  tower: string;
  unit: string;
  unitType: string;
  unitCategory: string;
  unitArea: string;
}

const overviewInrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function pickOverviewAmount(...values: unknown[]): string | number {
  for (const value of values) {
    if (value == null || value === '') {
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return '';
}

/** Returns true when an overview amount field has a displayable value. */
export function hasOverviewAmountValue(
  value: string | number | null | undefined,
): boolean {
  return value !== '' && value != null;
}

/** Formats overview currency fields (admin parity). */
export function formatOverviewInrAmount(
  value: string | number | null | undefined,
): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return '—';
  }
  return overviewInrFormatter.format(amount);
}

/** Maps parking details for booking overview (admin parity). */
export function mapBookingOverviewParkingDetails(
  booking: ApiRecord,
): BookingOverviewParkingDetails {
  const summary = rec(booking['summary']);
  const core = rec(booking['core']);
  const pricing = rec(core['pricingSnapshot']);
  const parkingDetails = rec(booking['parkingDetails']);
  return {
    parkingNo:
      field(parkingDetails, 'parkingNo') ||
      field(summary, 'parkingNo') ||
      field(pricing, 'parking_no') ||
      '',
    area:
      field(parkingDetails, 'area') ||
      field(summary, 'parkingArea') ||
      field(pricing, 'parking_area') ||
      '',
    type:
      field(parkingDetails, 'type') ||
      field(summary, 'parkingType') ||
      field(pricing, 'parking_type') ||
      '',
    amount: pickOverviewAmount(
      parkingDetails['amount'],
      summary['parkingAmount'],
      pricing['parking_amount'],
    ),
  };
}

/** Maps property grid fields for booking overview (admin parity). */
export function mapBookingOverviewPropertyBlock(
  booking: ApiRecord,
  fallbacks: Partial<BookingOverviewPropertyBlock> = {},
): BookingOverviewPropertyBlock {
  const summary = rec(booking['summary']);
  const core = rec(booking['core']);
  const pricing = rec(core['pricingSnapshot']);
  const tower = rec(booking['tower']);
  const unit = rec(booking['unit']);
  const unitSquareFeet =
    field(summary, 'unitSquareFeet') ||
    field(unit, 'unitSquareFeet') ||
    field(pricing, 'carpet_area_sqft') ||
    '';
  const unitArea = unitSquareFeet ? `${unitSquareFeet} Sq.Ft.` : '';

  return {
    project:
      field(summary, 'projectName') ||
      fallbacks.project ||
      '',
    tower:
      field(summary, 'towerName') ||
      fallbacks.tower ||
      '',
    unit:
      field(summary, 'unitNumber') ||
      field(unit, 'unitNumber') ||
      fallbacks.unit ||
      '',
    unitType:
      field(summary, 'unitType') ||
      field(unit, 'unitType') ||
      fallbacks.unitType ||
      '',
    unitCategory: resolveBookingUnitCategoryFromApi(
      booking,
      fallbacks.unitCategory ?? '',
    ),
    unitArea: unitArea || fallbacks.unitArea || '',
  };
}

/** Maps property amount breakdown for booking overview (admin parity). */
export function mapBookingOverviewPropertyAmount(
  booking: ApiRecord,
): BookingOverviewPropertyAmount {
  const summary = rec(booking['summary']);
  const core = rec(booking['core']);
  const pricing = rec(core['pricingSnapshot']);
  const paymentTotals = rec(booking['paymentTotals']);
  const agreementValue = resolveAgreementValueFromBooking(booking);
  return {
    agreementAmount: agreementValue > 0 ? agreementValue : '',
    gst: resolveGstAmountFromBooking(booking),
    tds: pickOverviewAmount(
      summary['tdsAmount'],
      paymentTotals['tds'],
      summary['tds'],
      pricing['tds_amount'],
    ),
    otherCharges: pickOverviewAmount(
      summary['otherChargesAmount'],
      paymentTotals['otherCharges'],
      summary['otherCharges'],
      pricing['otherChargesTotal'],
    ),
  };
}

export function computePropertyLoanAmount(
  propertyAmount: string | number,
  ownContribution: string | number,
): string {
  const total = parseAmountInput(propertyAmount);
  if (total <= 0) {
    return '';
  }
  const own = parseAmountInput(ownContribution);
  return String(Math.max(0, total - own));
}

export interface ResolvePropertyOwnerLoanFinanceInput {
  propertyAmount: string | number;
  bookingAmount?: string | number;
  ownContribution?: string | number;
  loanAmount?: string | number;
  financeType: 'self' | 'loan';
}

/** Resolves owner contribution and loan amount when finance type is loan. */
export function resolvePropertyOwnerLoanFinanceValues(
  input: ResolvePropertyOwnerLoanFinanceInput,
): { ownContribution: string; loanAmount: string } {
  if (input.financeType !== 'loan') {
    return {
      ownContribution: String(input.ownContribution ?? '').trim(),
      loanAmount: String(input.loanAmount ?? '').trim(),
    };
  }

  const propertyTotal = parseAmountInput(input.propertyAmount);
  let own = parseAmountInput(input.ownContribution ?? '');
  let loan = parseAmountInput(input.loanAmount ?? '');

  if (own <= 0) {
    const bookingAmt = parseAmountInput(input.bookingAmount ?? '');
    if (bookingAmt > 0) {
      own = bookingAmt;
    }
  }

  if (propertyTotal > 0) {
    const expectedLoan = Math.max(0, propertyTotal - own);
    if (loan <= 0) {
      loan = expectedLoan;
    }
  }

  return {
    ownContribution: own > 0 ? String(own) : '',
    loanAmount:
      loan > 0
        ? String(loan)
        : propertyTotal > 0
          ? computePropertyLoanAmount(input.propertyAmount, own)
          : '',
  };
}

export type PartyType = 'owner' | 'co_owner' | 'co_applicant';

export interface BookingDocumentPartyScope {
  partyType: PartyType;
  customerId?: string;
}

export interface BookingDocumentUploadPayload {
  documentType: string;
  fileUrl: string;
  version?: number;
  partyType?: PartyType;
  customerId?: string;
  originalFileName?: string;
}

export function buildBookingDocumentUploadPayload(
  documentType: string,
  fileUrl: string,
  scope?: BookingDocumentPartyScope,
  version?: number,
  originalFileName?: string,
): BookingDocumentUploadPayload {
  const payload: BookingDocumentUploadPayload = {
    documentType,
    fileUrl,
  };
  if (version !== undefined) {
    payload.version = version;
  }
  if (scope?.partyType) {
    payload.partyType = scope.partyType;
  }
  if (scope?.customerId?.trim()) {
    payload.customerId = scope.customerId.trim();
  }
  if (originalFileName?.trim()) {
    payload.originalFileName = originalFileName.trim();
  }
  return payload;
}

const CO_OWNER_DOCUMENT_PREFIX = 'co_owner_';
const CO_APPLICANT_DOCUMENT_PREFIX = 'co_applicant_';

function normalizeBookingDocumentType(value: string): string {
  return value.trim().toLowerCase().replace(/-/g, '_');
}

function normalizePartyType(value: string): PartyType {
  const normalized = value.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'co_owner') {
    return 'co_owner';
  }
  if (normalized === 'co_applicant' || normalized === 'coapplicant') {
    return 'co_applicant';
  }
  return 'owner';
}

function isCoPartyScopedDocumentType(documentType: string): boolean {
  const normalized = normalizeBookingDocumentType(documentType);
  return (
    normalized.startsWith(CO_OWNER_DOCUMENT_PREFIX) ||
    normalized.startsWith(CO_APPLICANT_DOCUMENT_PREFIX)
  );
}

function normalizeCustomerId(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function isCoPartyScope(partyType: PartyType): boolean {
  return partyType === 'co_owner' || partyType === 'co_applicant';
}

function coPartyTypesEquivalent(docParty: PartyType, scopeParty: PartyType): boolean {
  if (docParty === scopeParty) {
    return true;
  }
  return isCoPartyScope(docParty) && isCoPartyScope(scopeParty);
}

/** Whether a stored booking document belongs to the active party scope. */
export function isBookingDocumentInPartyScope(
  documentType: string,
  partyType: PartyType,
): boolean {
  const normalized = normalizeBookingDocumentType(documentType);

  if (partyType === 'owner') {
    return !isCoPartyScopedDocumentType(normalized);
  }
  if (partyType === 'co_applicant') {
    return (
      normalized.startsWith(CO_APPLICANT_DOCUMENT_PREFIX) ||
      normalized.startsWith(CO_OWNER_DOCUMENT_PREFIX)
    );
  }

  return normalized.startsWith(CO_OWNER_DOCUMENT_PREFIX);
}

function documentMatchesPartyScope(
  doc: ApiRecord,
  scope: BookingDocumentPartyScope,
): boolean {
  const docPartyType = field(doc, 'partyType', '');
  const docCustomerId = field(doc, 'customerId', '');
  const documentType = field(doc, 'documentType', field(doc, 'document_type'));

  if (docPartyType || docCustomerId) {
    const normalizedDocParty = normalizePartyType(docPartyType || 'owner');
    if (!coPartyTypesEquivalent(normalizedDocParty, scope.partyType)) {
      return false;
    }
    if (scope.partyType !== 'owner' && scope.customerId) {
      if (docCustomerId) {
        return normalizeCustomerId(docCustomerId) === normalizeCustomerId(scope.customerId);
      }
      return isBookingDocumentInPartyScope(documentType, scope.partyType);
    }
    if (scope.customerId && docCustomerId) {
      return normalizeCustomerId(docCustomerId) === normalizeCustomerId(scope.customerId);
    }
    return scope.partyType === 'owner';
  }

  if (scope.partyType !== 'owner' && scope.customerId) {
    return isBookingDocumentInPartyScope(documentType, scope.partyType);
  }

  return isBookingDocumentInPartyScope(documentType, scope.partyType);
}

/** Filters booking documents for a primary owner or a specific co-owner party. */
export function filterBookingDocumentsForPartyScope(
  documents: readonly ApiRecord[],
  scope: BookingDocumentPartyScope,
): ApiRecord[] {
  return documents.filter((doc) => documentMatchesPartyScope(doc, scope));
}

export interface CustomerKycPayloadInput {
  panEntityType: 'individual' | 'non-individual';
  panNumber: string;
  aadhaarNumber: string;
  panVerified: boolean;
  aadhaarVerified: boolean;
  partyType: PartyType;
  customerId?: string;
  panKyc: PropertyOwnerPanKycFields;
  idpAadhaarProfile?: IdpAadhaarExtractedData | null;
  alreadyPanVerified?: boolean;
  alreadyAadhaarVerified?: boolean;
}

export function buildCustomerKycPayload(state: CustomerKycPayloadInput): Record<string, unknown> {
  const kycDetails = mergeIdpAadhaarIntoKycDetails(state.panKyc, state.idpAadhaarProfile);
  const alreadyPanVerified = state.alreadyPanVerified === true;
  const alreadyAadhaarVerified = state.alreadyAadhaarVerified === true;
  const payload: Record<string, unknown> = {
    ownerType: state.panEntityType === 'non-individual' ? 'non_individual' : 'individual',
    panVerified: state.panVerified,
    aadhaarVerified: state.aadhaarVerified,
    verificationMethod: 'manual',
    partyType: state.partyType,
    kycDetails,
    alreadyPanVerified,
    alreadyAadhaarVerified,
  };
  if (!alreadyPanVerified && state.panNumber.trim()) {
    payload['panNumber'] = state.panNumber.trim();
  }
  if (!alreadyAadhaarVerified && state.aadhaarNumber.trim()) {
    payload['aadhaarNumber'] = state.aadhaarNumber.trim();
  }
  if (state.customerId?.trim()) {
    payload['customerId'] = state.customerId.trim();
  }
  return payload;
}

export function buildCustomerOccupationPayload(
  occType: 'salaried' | 'self-employed',
  salaried: Record<string, string>,
  selfEmployed: Record<string, string>,
  partyType: PartyType,
  customerId?: string,
): Record<string, unknown> {
  const isSalaried = occType === 'salaried';
  const occ = isSalaried ? salaried : selfEmployed;

  let occupation: Record<string, unknown> = {
    employmentType: isSalaried ? 'salaried' : 'self-employed',
  };

  if (isSalaried) {
    occupation = {
      ...occupation,
      industryType: occ['industryType'] || undefined,
      organizationName: occ['organisationName'] || undefined,
      experienceCurrent: occ['workExpCurrent'] || undefined,
      experienceTotal: occ['totalWorkExp'] || undefined,
      monthlySalary: parseAmountInput(occ['monthlySalary']) || undefined,
      officeEmail: occ['officeEmail'] || undefined,
      officeAddressLine: occ['officeAddressLine'] || undefined,
      officeCountryId: occ['officeCountry'] || undefined,
      officeStateId: occ['officeState'] || undefined,
      officeCityId: occ['officeCity'] || undefined,
      officePinCode: occ['officePin'] || undefined,
    };
  } else {
    occupation = {
      ...occupation,
      businessNature: occ['businessNature'] || undefined,
      businessName: occ['businessName'] || undefined,
      businessAge: occ['businessAge'] || undefined,
      monthlyIncome: parseAmountInput(occ['monthlyIncome']) || undefined,
      annualIncome: parseAmountInput(occ['annualIncome']) || undefined,
      businessEmail: occ['businessEmail'] || undefined,
      constitution: occ['constitution'] || undefined,
      ownershipPct: occ['ownershipPct'] || undefined,
      addressLine: occ['addressLine'] || undefined,
      country: occ['country'] || undefined,
      state: occ['state'] || undefined,
      city: occ['city'] || undefined,
      pin: occ['pin'] || undefined,
    };
  }

  const payload: Record<string, unknown> = {
    partyType,
    occupation,
  };
  if (customerId?.trim()) {
    payload['customerId'] = customerId.trim();
  }
  return payload;
}

export function buildCustomerFinancialPayload(
  fin: Record<string, string>,
  partyType: PartyType,
  customerId?: string,
  options?: {
    financeType?: 'self' | 'loan';
    ownContribution?: string;
    loanAmount?: string;
    tenureMonths?: number;
  },
): Record<string, unknown> {
  const financial: Record<string, unknown> = {
    salaryCreditBank: fin['salaryCreditBank'] || undefined,
    creditMode: fin['creditMode'] || undefined,
    netMonthlySalary: parseAmountInput(fin['netMonthlySalary']) || undefined,
    otherMonthlyIncome: parseAmountInput(fin['otherMonthlyIncome']) || undefined,
    currentEmi: parseAmountInput(fin['currentEmiTotal']) || undefined,
    creditCardOutstanding: parseAmountInput(fin['creditCardOutstanding']) || undefined,
  };

  if (options?.financeType === 'loan') {
    financial['financeType'] = 'loan';
    const ownContribution = parseAmountInput(options.ownContribution);
    const loanAmount = parseAmountInput(options.loanAmount);
    if (ownContribution > 0) {
      financial['ownContribution'] = ownContribution;
    }
    if (loanAmount > 0) {
      financial['loanAmount'] = loanAmount;
    }
    const tenureMonths = Number(options.tenureMonths);
    if (
      Number.isFinite(tenureMonths) &&
      tenureMonths >= 1 &&
      tenureMonths <= 360
    ) {
      financial['tenureMonths'] = Math.round(tenureMonths);
    }
  } else if (options?.financeType === 'self') {
    financial['financeType'] = 'self';
  }

  const payload: Record<string, unknown> = {
    partyType,
    financial,
  };
  if (customerId?.trim()) {
    payload['customerId'] = customerId.trim();
  }
  return payload;
}

export function applyOwnerWorkflowToForms(
  workflow: ApiRecord,
  targets: {
    panEntityType: { value: 'individual' | 'non-individual' };
    panNumber: { value: string };
    panVerified: { value: boolean };
    aadhaarNumber?: { value: string };
    aadhaarVerified?: { value: boolean };
    panKyc: PropertyOwnerPanKycFields;
    occType: { value: 'salaried' | 'self-employed' };
    occSalaried: Record<string, string>;
    occSelf: Record<string, string>;
    fin: Record<string, string>;
    financeType: { value: 'self' | 'loan' };
  },
): void {
  const kyc = rec(workflow['kyc']);
  const pan = rec(kyc['pan']);
  const aadhaar = rec(kyc['aadhaar']);
  const identity = rec(kyc['identity']);
  const contact = rec(kyc['contact']);
  const residential = rec(kyc['residential']);
  const entityType = field(kyc, 'entityType', 'individual');

  targets.panEntityType.value =
    entityType === 'non_individual' ? 'non-individual' : 'individual';
  targets.panNumber.value = resolvePanDisplayNumber(workflow);
  targets.panVerified.value =
    pan['verified'] === true ||
    workflow['panVerified'] === true ||
    kyc['panVerified'] === true;

  if (targets.aadhaarNumber) {
    targets.aadhaarNumber.value = extractAadhaarDisplayNumber(workflow);
  }
  if (targets.aadhaarVerified) {
    targets.aadhaarVerified.value =
      aadhaar['verified'] === true || !!targets.aadhaarNumber?.value;
  }

  targets.panKyc['namePrefixId'] =
    field(identity, 'namePrefixId', targets.panKyc.namePrefixId ?? '') ||
    field(workflow, 'namePrefixId', targets.panKyc.namePrefixId ?? '') ||
    null;
  if (
    targets.panKyc.namePrefixId === '' ||
    targets.panKyc.namePrefixId === undefined
  ) {
    targets.panKyc.namePrefixId = null;
  }
  targets.panKyc.fullName = field(identity, 'fullName', targets.panKyc.fullName ?? '');
  targets.panKyc['gender'] =
    normalizeGenderForApi(field(identity, 'gender', targets.panKyc['gender'] ?? '')) ??
    field(identity, 'gender', targets.panKyc['gender'] ?? '');
  targets.panKyc['dateOfBirth'] =
    formatToIsoDate(field(identity, 'dateOfBirth', targets.panKyc['dateOfBirth'] ?? '')) ??
    field(identity, 'dateOfBirth', targets.panKyc['dateOfBirth'] ?? '');
  targets.panKyc['maritalStatus'] = normalizeMaritalStatusForDisplay(
    field(contact, 'maritalStatus', targets.panKyc['maritalStatus'] ?? ''),
  );
  targets.panKyc['email'] = field(contact, 'email', targets.panKyc['email'] ?? '');
  targets.panKyc['mobile'] = field(contact, 'mobile', targets.panKyc['mobile'] ?? '');
  targets.panKyc['addressLine'] = field(residential, 'addressLine', targets.panKyc['addressLine'] ?? '');
  targets.panKyc['city'] = field(residential, 'city', targets.panKyc['city'] ?? '');
  targets.panKyc['state'] = field(residential, 'state', targets.panKyc['state'] ?? '');
  targets.panKyc['country'] = field(residential, 'country', targets.panKyc['country'] ?? '');
  targets.panKyc['pinCode'] = field(residential, 'pinCode', targets.panKyc['pinCode'] ?? '');

  const occ = rec(workflow['occupation']);
  const employmentType = field(occ, 'employmentType', field(occ, 'type', 'salaried'));
  const normalizedType = employmentType.replace(/-/g, '_').toLowerCase();
  targets.occType.value = normalizedType === 'self_employed' ? 'self-employed' : 'salaried';

  if (targets.occType.value === 'salaried') {
    const sal = rec(occ['salaried']);
    const salFlat = Object.keys(sal).length ? sal : occ;
    targets.occSalaried['industryType'] = field(salFlat, 'industryType');
    targets.occSalaried['organisationName'] = field(salFlat, 'organisationName', field(salFlat, 'organizationName'));
    targets.occSalaried['workExpCurrent'] = field(salFlat, 'workExpCurrent', field(salFlat, 'experienceCurrent'));
    targets.occSalaried['totalWorkExp'] = field(salFlat, 'totalWorkExp', field(salFlat, 'experienceTotal'));
    targets.occSalaried['monthlySalary'] = field(salFlat, 'monthlySalary', field(salFlat, 'monthlyIncome', '0'));
    targets.occSalaried['officeEmail'] = field(salFlat, 'officeEmail');
    targets.occSalaried['officeAddressLine'] = field(
      salFlat,
      'officeAddressLine',
      field(salFlat, 'officeAddress'),
    );
    targets.occSalaried['officeCountry'] = field(salFlat, 'officeCountryId', field(salFlat, 'officeCountry'));
    targets.occSalaried['officeState'] = field(salFlat, 'officeStateId', field(salFlat, 'officeState'));
    targets.occSalaried['officeCity'] = field(salFlat, 'officeCityId', field(salFlat, 'officeCity'));
    targets.occSalaried['officePin'] = field(salFlat, 'officePinCode', field(salFlat, 'officePin'));
  } else {
    const self = rec(occ['selfEmployed']);
    const selfFlat = Object.keys(self).length ? self : occ;
    targets.occSelf['businessNature'] = field(selfFlat, 'businessNature', field(selfFlat, 'natureOfBusiness'));
    targets.occSelf['businessName'] = field(selfFlat, 'businessName');
    targets.occSelf['businessAge'] = field(selfFlat, 'businessAge');
    targets.occSelf['monthlyIncome'] = field(selfFlat, 'monthlyIncome', '0');
    targets.occSelf['annualIncome'] = field(selfFlat, 'annualIncome', '0');
    targets.occSelf['businessEmail'] = field(selfFlat, 'businessEmail');
    targets.occSelf['constitution'] = field(selfFlat, 'constitution');
    targets.occSelf['ownershipPct'] = field(selfFlat, 'ownershipPct', '100');
    targets.occSelf['addressLine'] = field(selfFlat, 'addressLine', field(selfFlat, 'businessAddress'));
    targets.occSelf['country'] = field(selfFlat, 'country');
    targets.occSelf['state'] = field(selfFlat, 'state');
    targets.occSelf['city'] = field(selfFlat, 'city');
    targets.occSelf['pin'] = field(selfFlat, 'pin');
  }

  const fin = rec(workflow['financial']);
  targets.fin['salaryCreditBank'] = field(
    fin,
    'salaryCreditBank',
    field(fin, 'salary_credit_bank'),
  );
  targets.fin['creditMode'] = field(
    fin,
    'creditMode',
    field(fin, 'credit_mode', field(fin, 'creditModeBank')),
  );
  targets.fin['netMonthlySalary'] = field(
    fin,
    'netMonthlySalary',
    field(fin, 'net_monthly_salary', '0'),
  );
  targets.fin['otherMonthlyIncome'] = field(
    fin,
    'otherMonthlyIncome',
    field(fin, 'other_monthly_income', '0'),
  );
  targets.fin['currentEmiTotal'] = field(
    fin,
    'currentEmi',
    field(fin, 'currentEmiTotal', field(fin, 'current_emi', '0')),
  );
  targets.fin['creditCardOutstanding'] = field(
    fin,
    'creditCardOutstanding',
    field(fin, 'credit_card_outstanding', '0'),
  );

  const bookingSummary = rec(workflow['bookingSummary']);
  const ft = field(bookingSummary, 'financeType');
  if (ft === 'loan' || ft === 'home_loan') {
    targets.financeType.value = 'loan';
  } else if (ft === 'self') {
    targets.financeType.value = 'self';
  }
}

export {
  applyTimelineSteps,
  isBookingCancelledStatus,
  resolveBookingStepIndex,
} from './utils/booking-pipeline-step.util';

export function createEmptyDocumentRows(): DocumentUploadRow[] {
  return createDocumentRowsForEmploymentType('salaried');
}

export function createDocumentRowsForEmploymentType(
  employmentType: string,
): DocumentUploadRow[] {
  const normalized = String(employmentType ?? '')
    .trim()
    .toLowerCase()
    .replaceAll('_', '-');
  const isSelfEmployed =
    normalized === 'self-employed' ||
    normalized === 'selfemployed' ||
    normalized === 'self employed';

  const common: DocumentUploadRow[] = [
    { id: 'pan', name: 'PAN Card', required: true, files: [] },
    { id: 'aadhar', name: 'Aadhar Card', required: true, files: [] },
    { id: 'photo', name: 'Passport Size Photo', required: true, files: [] },
  ];

  if (isSelfEmployed) {
    return [
      ...common,
      {
        id: 'bank',
        name: 'Bank Statements-6 Months (Business/Current A/C)',
        required: true,
        files: [],
      },
      {
        id: 'loan',
        name: 'Existing Loan Statements-12 Months (Optional)',
        required: false,
        files: [],
      },
    ];
  }

  return [
    ...common,
    { id: 'itr', name: 'Form No 16/ITR Ack (Latest)', required: true, files: [] },
    { id: 'salary', name: 'Salary Slips -(Last 3)', required: true, files: [] },
    { id: 'bank', name: 'Bank Statements-6 Months (Salary A/C)', required: true, files: [] },
    {
      id: 'loan',
      name: 'Existing Loan Statements-12 Months (Optional)',
      required: false,
      files: [],
    },
  ];
}

function mergePreservedDocumentRowFiles(
  hydratedRows: DocumentUploadRow[],
  previousRows: readonly DocumentUploadRow[],
): DocumentUploadRow[] {
  return hydratedRows.map((row) => {
    const previous = previousRows.find((item) => item.id === row.id);
    if (!previous?.files.length) {
      return row;
    }
    const merged = [...row.files];
    for (const file of previous.files) {
      const exists = merged.some(
        (existing) =>
          existing.id === file.id ||
          (existing.serverId && file.serverId && existing.serverId === file.serverId),
      );
      if (!exists) {
        merged.push(file);
      }
    }
    return { ...row, files: merged };
  });
}

/** Rebuilds document rows for employment type while keeping API + in-session uploads. */
export function rebuildPropertyOwnerDocumentRowsForEmploymentType(
  employmentType: string,
  previousRows: readonly DocumentUploadRow[],
  apiDocuments: readonly ApiRecord[],
  scope?: BookingDocumentPartyScope,
): DocumentUploadRow[] {
  const template = createDocumentRowsForEmploymentType(employmentType);
  const hydrated = hydratePropertyOwnerDocumentRows(template, apiDocuments, scope);
  return mergePreservedDocumentRowFiles(hydrated, previousRows);
}

const ROW_DOCUMENT_TYPE_ALIASES: Record<string, readonly string[]> = {
  pan: ['pan_card', 'pan'],
  aadhar: ['aadhaar_card', 'aadhar_card', 'aadhaar', 'aadhaar_front', 'aadhaar_back'],
  photo: ['passport_photo', 'passport_size_photo'],
  itr: ['form16_or_itr', 'form_16', 'itr', 'form_16_or_itr'],
  salary: ['salary_slips', 'salary_slip'],
  bank: ['bank_statements', 'bank_statement'],
  loan: ['existing_loan_statements', 'loan_statements'],
};

function stripPartyScopedDocumentTypePrefix(documentType: string): string {
  const normalized = normalizeBookingDocumentType(documentType);
  if (normalized.startsWith(CO_APPLICANT_DOCUMENT_PREFIX)) {
    return normalized.slice(CO_APPLICANT_DOCUMENT_PREFIX.length);
  }
  if (normalized.startsWith(CO_OWNER_DOCUMENT_PREFIX)) {
    return normalized.slice(CO_OWNER_DOCUMENT_PREFIX.length);
  }
  return normalized;
}

function typesMatch(candidate: string, target: string): boolean {
  const normalizedCandidate = normalizeBookingDocumentType(candidate);
  const normalizedTarget = normalizeBookingDocumentType(target);
  return (
    normalizedCandidate === normalizedTarget ||
    normalizedCandidate.replace(/_/g, '') === normalizedTarget.replace(/_/g, '')
  );
}

function documentTypeMatchesRow(rowId: string, documentType: string): boolean {
  const normalized = stripPartyScopedDocumentTypePrefix(documentType);
  const aliases = ROW_DOCUMENT_TYPE_ALIASES[rowId] ?? [];
  const mapped = DOCUMENT_ROW_TYPE_MAP[rowId];
  const candidates = mapped ? [...aliases, mapped] : aliases;
  return candidates.some((candidate) => typesMatch(normalized, candidate));
}

/** Maps a wizard document row to the API document type for the active party. */
export function mapPropertyOwnerDocumentRowType(
  rowId: string,
  partyType: PartyType = 'owner',
): string {
  const base = DOCUMENT_ROW_TYPE_MAP[rowId];
  if (!base) {
    return rowId;
  }
  if (partyType === 'co_applicant') {
    return `CO_APPLICANT_${base.toUpperCase()}`;
  }
  if (partyType === 'co_owner') {
    return `CO_OWNER_${base.toUpperCase()}`;
  }
  return base;
}

/** Maps KYC upload types (PAN / Aadhaar sides) to the scoped API document type. */
export function mapKycUploadDocumentType(
  baseType: 'PAN' | 'AADHAAR_FRONT' | 'AADHAAR_BACK',
  partyType: PartyType = 'owner',
): string {
  if (partyType === 'co_applicant') {
    return `CO_APPLICANT_${baseType}`;
  }
  if (partyType === 'co_owner') {
    return `CO_OWNER_${baseType}`;
  }
  return baseType;
}

function getDocumentCreatedAt(doc: ApiRecord): number {
  const raw = doc['createdAt'] ?? doc['created_at'];
  const parsed = raw ? Date.parse(String(raw)) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortBookingDocumentsByRecency(
  documents: readonly ApiRecord[],
): ApiRecord[] {
  return [...documents].sort(
    (left, right) => getDocumentCreatedAt(right) - getDocumentCreatedAt(left),
  );
}

function normalizeLabelKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function looksLikeGeneratedDocumentLabel(
  displayName: string,
  documentType: string,
  rowLabel: string,
): boolean {
  const nameKey = normalizeLabelKey(displayName.replace(/\.[^.]+$/, ''));
  if (!nameKey) {
    return true;
  }

  const candidates = [
    stripPartyScopedDocumentTypePrefix(documentType),
    documentType,
    rowLabel,
    toSentenceCase(stripPartyScopedDocumentTypePrefix(documentType)),
    toSentenceCase(documentType),
    toSentenceCase(rowLabel),
  ];

  return candidates.some((candidate) => {
    const key = normalizeLabelKey(candidate);
    return key.length > 0 && key === nameKey;
  });
}

function extractReadableNameFromFileUrl(url: string): string | null {
  const segment = url.split('/').filter(Boolean).pop()?.split('?')[0];
  if (!segment || segment === 'upload' || !segment.includes('.')) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(segment);
    if (decoded.trim() && !isUuidLikeFileName(decoded)) {
      return sanitizeOriginalFileName(decoded);
    }
  } catch {
    if (segment.trim() && !isUuidLikeFileName(segment)) {
      return sanitizeOriginalFileName(segment);
    }
  }

  return null;
}

function isUploadedApiDocument(doc: ApiRecord): boolean {
  const fileUrl = field(doc, 'fileUrl');
  const serverId = field(doc, 'id', field(doc, 'documentId'));
  return Boolean(serverId || fileUrl);
}

function resolveDocumentDisplayName(row: DocumentUploadRow, doc: ApiRecord): string {
  const documentType = field(doc, 'documentType', field(doc, 'document_type'));
  const explicit = field(
    doc,
    'fileName',
    field(
      doc,
      'originalFileName',
      field(doc, 'original_file_name', field(doc, 'name')),
    ),
  ).trim();
  const url = field(doc, 'fileUrl', field(doc, 'file_url'));
  const urlName = url ? extractReadableNameFromFileUrl(url) : null;

  if (
    explicit &&
    !looksLikeGeneratedDocumentLabel(explicit, documentType, row.name)
  ) {
    return explicit;
  }

  if (urlName) {
    return urlName;
  }

  if (explicit) {
    return explicit;
  }

  const type = normalizeBookingDocumentType(documentType);
  if (type.includes('pan')) {
    return 'PAN_Card.pdf';
  }
  if (type.includes('aadhaar') || type.includes('aadhar')) {
    return 'Aadhaar_Card.pdf';
  }
  return `${row.name.replace(/[^\w]+/g, '_')}.pdf`;
}

function parseDocumentSizeBytes(doc: ApiRecord): number | undefined {
  const raw = doc['fileSize'] ?? doc['sizeBytes'] ?? doc['size'];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const digits = raw.replace(/[^\d.]/g, '');
    const parsed = Number.parseFloat(digits);
    if (Number.isFinite(parsed)) {
      if (/mb/i.test(raw)) {
        return Math.round(parsed * 1024 * 1024);
      }
      if (/kb/i.test(raw)) {
        return Math.round(parsed * 1024);
      }
      return Math.round(parsed);
    }
  }
  return undefined;
}

function toUploadedFileFromApi(row: DocumentUploadRow, doc: ApiRecord): UploadedDocumentFile | null {
  if (!isUploadedApiDocument(doc)) {
    return null;
  }

  const serverId = field(doc, 'id', field(doc, 'documentId'));
  return {
    id: serverId ? `api-${serverId}` : `api-${row.id}-${field(doc, 'documentType')}`,
    name: resolveDocumentDisplayName(row, doc),
    sizeBytes: parseDocumentSizeBytes(doc),
    serverId: serverId || undefined,
  };
}

function upsertApiDocumentFile(row: DocumentUploadRow, doc: ApiRecord): void {
  const uploaded = toUploadedFileFromApi(row, doc);
  if (!uploaded) {
    return;
  }

  const existingIndex = row.files.findIndex(
    (file) =>
      (!!uploaded.serverId && file.serverId === uploaded.serverId) || file.id === uploaded.id,
  );
  if (existingIndex >= 0) {
    row.files[existingIndex] = { ...row.files[existingIndex], ...uploaded };
    row.files = [...row.files];
    return;
  }

  row.files = [...row.files, uploaded];
}

/** Maps booking `documents[]` from GET /v1/bookings/:id onto wizard upload rows. */
export function hydratePropertyOwnerDocumentRows(
  rows: DocumentUploadRow[],
  apiDocuments: readonly ApiRecord[],
  scope?: BookingDocumentPartyScope,
): DocumentUploadRow[] {
  const scopedDocuments = sortBookingDocumentsByRecency(
    scope
      ? filterBookingDocumentsForPartyScope(apiDocuments, scope)
      : [...apiDocuments],
  );
  const hydrated = rows.map((row) => ({ ...row, files: [...row.files] }));
  if (!scopedDocuments.length) {
    return hydrated;
  }

  for (const row of hydrated) {
    const matches = scopedDocuments.filter(
      (doc) => documentTypeMatchesRow(row.id, field(doc, 'documentType')),
    );
    if (!matches.length) {
      continue;
    }

    if (row.id === 'aadhar') {
      const preferred =
        matches.find((doc) =>
          normalizeBookingDocumentType(field(doc, 'documentType')).includes('front'),
        ) ?? matches[0];
      upsertApiDocumentFile(row, preferred);
      continue;
    }

    if (row.id === 'salary') {
      for (const doc of matches.slice(0, 3)) {
        upsertApiDocumentFile(row, doc);
      }
      continue;
    }

    upsertApiDocumentFile(row, matches[0]);
  }

  return hydrated;
}

export function extractBookingDocumentsFromResponse(data: ApiRecord): ApiRecord[] {
  const documents = data['documents'];
  if (!Array.isArray(documents)) {
    return [];
  }
  return documents.filter(
    (item): item is ApiRecord => !!item && typeof item === 'object' && !Array.isArray(item),
  );
}

function pickLegacySourceRecord(apiData: ApiRecord): ApiRecord {
  const summary = rec(apiData['summary']);
  const summarySource = rec(summary['source']);
  if (Object.keys(summarySource).length > 0) {
    return summarySource;
  }
  const rootSource = rec(apiData['source']);
  if (Object.keys(rootSource).length > 0) {
    return rootSource;
  }
  return {};
}

function pickSourceDetailsRecord(apiData: ApiRecord): ApiRecord {
  const rootSourceDetails = rec(apiData['sourceDetails']);
  if (Object.keys(rootSourceDetails).length > 0) {
    return rootSourceDetails;
  }

  const enquiry = rec(apiData['enquiry']);
  const enquiryText = rec(enquiry['enquiryText']);
  const enquirySourceDetails = rec(enquiryText['sourceDetails']);
  if (Object.keys(enquirySourceDetails).length > 0) {
    return enquirySourceDetails;
  }

  return {};
}

/** Resolves a person/name value that may be a string or nested object. */
function resolvePersonName(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  const record = rec(value);
  return (
    field(record, 'fullName') ||
    field(record, 'name') ||
    field(record, 'displayName') ||
    field(record, 'leadOwnerName') ||
    ''
  );
}

/** Maps booking enquiry source fields for the Source Details summary card. */
export function resolveEnquirySourceDetails(
  apiData: ApiRecord | null | undefined,
): { sourceType: string; name: string; leadOwner: string } {
  if (!apiData) {
    return { sourceType: '', name: '', leadOwner: '' };
  }

  const summary = rec(apiData['summary']);
  const summarySource = rec(summary['source']);
  const sourceDetails = pickSourceDetailsRecord(apiData);
  const lead = rec(apiData['lead']);

  let rawSourceType = field(sourceDetails, 'sourceType');
  let channelPartnerName = field(sourceDetails, 'channelPartnerName');
  let referralName = field(sourceDetails, 'referralName');

  if (!rawSourceType && !channelPartnerName && !referralName) {
    const legacySource = pickLegacySourceRecord(apiData);
    rawSourceType = field(legacySource, 'leadType', field(legacySource, 'leadSource'));
    channelPartnerName = field(legacySource, 'channelPartnerName');
    referralName = field(legacySource, 'referralName');
  }

  const leadOwner =
    field(summarySource, 'leadOwnerName') ||
    field(summarySource, 'leadOwner') ||
    field(sourceDetails, 'leadOwner') ||
    resolvePersonName(apiData['opportunityOwner']) ||
    resolvePersonName(lead['owner']) ||
    resolvePersonName(lead['leadOwner']) ||
    field(lead, 'leadOwnerName') ||
    resolvePersonName(apiData['leadOwner']);

  return {
    sourceType: rawSourceType,
    name: channelPartnerName || referralName,
    leadOwner,
  };
}

/** Resolves car parking display from booking API parking details. */
export function resolveParkingNo(apiData: ApiRecord): string {
  const parkingDetails = rec(apiData['parkingDetails']);
  const summary = rec(apiData['summary']);
  const core = rec(apiData['core']);
  const pricingSnapshot = rec(core['pricingSnapshot']);
  const unit = rec(apiData['unit']);

  return (
    field(parkingDetails, 'parkingNo') ||
    field(summary, 'parkingNo') ||
    field(pricingSnapshot, 'parking_no') ||
    field(unit, 'carParking') ||
    field(summary, 'carParking') ||
    ''
  );
}

/** Removes a trailing ` - Unit {label}` when the same label already appears earlier. */
export function normalizeBookingUnitNumberDisplay(unitNumber: string): string {
  const trimmed = unitNumber.trim();
  if (!trimmed) {
    return '';
  }
  const trailing = / - Unit ([^-]+)$/.exec(trimmed);
  if (!trailing?.[1]) {
    return trimmed;
  }
  const suffix = trailing[1].trim();
  const prefix = trimmed.slice(0, trailing.index).trim();
  if (
    prefix.includes(`Unit ${suffix}`) ||
    /Unit ID:\s*\d+/i.test(prefix)
  ) {
    return prefix;
  }
  return trimmed;
}

function unitAreaAlreadyPresentInUnitType(
  unitType: string,
  unitArea: string,
): boolean {
  const type = unitType.trim();
  const area = unitArea.trim();
  if (!type || !area) {
    return false;
  }
  if (type.includes(area)) {
    return true;
  }
  const normalizeSqFtToken = (value: string): string =>
    value
      .replace(/\(\s*/g, ' ')
      .replace(/\)\s*/g, ' ')
      .replace(/\s*sq\.?\s*ft\.?\s*/gi, ' sqft ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  const typeNorm = normalizeSqFtToken(type);
  const areaNorm = normalizeSqFtToken(area);
  if (areaNorm && typeNorm.includes(areaNorm)) {
    return true;
  }
  const areaDigits = area.replace(/[^\d.]/g, '');
  return Boolean(areaDigits) && type.includes(areaDigits);
}

/** Single-line unit descriptor for booking payment card (admin parity). */
export function formatBookingPaymentUnitLine(params: {
  towerName?: string;
  unit?: string;
  unitType?: string;
  unitArea?: string;
}): string {
  const unit = normalizeBookingUnitNumberDisplay(params.unit ?? '').trim();
  const unitType = params.unitType?.trim();
  const unitArea = params.unitArea?.trim();

  const typeSegment =
    unitType &&
    unitArea &&
    !unitAreaAlreadyPresentInUnitType(unitType, unitArea)
      ? `${unitType} (${unitArea})`
      : unitType || unitArea || '';

  const segments: string[] = [];
  if (unit) {
    const unitSegment = /^unit\s/i.test(unit) ? unit : `Unit ${unit}`;
    segments.push(unitSegment);
  }
  if (typeSegment) {
    segments.push(typeSegment);
  }
  return segments.join(' | ');
}
